'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/scale-extra'

const AttentionMasks = dynamic(() => import('../_components/widgets/ScaleWidgets').then(m => m.AttentionMasks), { ssr: false })

export default function EncodersAndDecoders() {
  return (
    <Lesson>
      <Step>
        <H>Three shapes of transformer</H>
        <P>
          Your tiny GPT is one kind of transformer: a <strong>decoder</strong>. Every token may only look backwards, because
          its job is to predict what comes next. But the same blocks can be wired up in other ways, and the difference is
          mostly <em>which tokens may attend to which</em>, set by a mask.
        </P>
        <UL>
          <li>
            <strong>Decoder-only</strong> (GPT, Llama and most chat models): a causal mask, trained to predict the next token. Built to
            write.
          </li>
          <li>
            <strong>Encoder-only</strong> (BERT): no mask at all, so every token sees the whole text, in both directions. Trained
            to fill in blanks. Built to understand: search, classification, embeddings.
          </li>
          <li>
            <strong>Encoder–decoder</strong> (the original 2017 Transformer, T5): an encoder reads the input, and a decoder
            writes the output while looking back at the encoder through <strong>cross-attention</strong>. Built to transform
            one sequence into another, as in translation.
          </li>
        </UL>
        <Figure n="9.7" wide caption="Each row is a query token, each column a key it might attend to. Coloured = allowed. Hover or tap a row.">
          <AttentionMasks />
        </Figure>
      </Step>

      <Step gate="masks">
        <H>Masks in code</H>
        <P>
          A mask is applied just before the softmax: hidden positions get a score of minus infinity, which becomes a weight of
          exactly zero. You’ve been using <C>maskCausal()</C> in the tiny GPT; now write it yourself.
        </P>
        <Exercise
          id="masks"
          title="causalMask and maskedSoftmax"
          {...EX.masks}
          hints={[
            <>
              <C>{'range(n).map(i => range(n).map(j => (j <= i ? 1 : 0)))'}</C>
            </>,
            <>
              Per row: find the max over the allowed scores only, take <C>Math.exp(s − max)</C> where allowed and 0 elsewhere,
              then divide by the total.
            </>,
          ]}
        />
      </Step>

      <Step gate="cross-attention">
        <H>Cross-attention</H>
        <P>
          In cross-attention, the <strong>queries</strong> come from the decoder (the sentence being written) while the{' '}
          <strong>keys and values</strong> come from the encoder (the sentence being read). So the weights form an{' '}
          <em>m × n</em> grid: every output word gets a probability distribution over every input word.
        </P>
        <Exercise
          id="cross-attention"
          title="attend"
          {...EX.crossAttention}
          hints={[
            <>
              <C>{'const scores = mat.scale(mat.mul(Q, mat.transpose(K)), 1 / Math.sqrt(d))'}</C>
            </>,
            <>
              <C>{'const weights = scores.map(row => vec.softmax(row))'}</C>, then <C>{'mat.mul(weights, V)'}</C>.
            </>,
          ]}
        />
        <Callout kind="note">
          Real translation models learn their alignments. When translating “the black cat” into French (“le chat noir”), the
          word order flips. The cross-attention weights learn to cross over, so “noir” looks back at “black”.
        </Callout>
      </Step>

      <Step gate={['masked-lm', 'q-arch']}>
        <H>Filling in blanks</H>
        <P>
          BERT’s training trick: hide about 15% of the words and train the model to recover them, using context from{' '}
          <em>both</em> sides. It never learns to write, but it learns rich representations of meaning, which is why encoders
          still power a lot of search engines and the retrieval systems you’ll meet in Module 10.
        </P>
        <Exercise
          id="masked-lm"
          title="makeExample and pickPositions"
          {...EX.maskedLM}
          hints={[
            <>
              <C>{'const hidden = new Set(positions)'}</C>, then map the words to <C>{'"[MASK]"'}</C> where hidden; the targets are{' '}
              <C>{'positions.map(i => words[i])'}</C>.
            </>,
            <>
              <C>{'random.shuffle(range(n)).slice(0, Math.round(n * rate)).sort((a, b) => a - b)'}</C>
            </>,
          ]}
        />
        <Quiz
          id="q-arch"
          question="You want a model that sorts customer emails into 12 categories. Which architecture fits most naturally?"
          options={[
            { label: 'An encoder: it reads the whole email in both directions, and a small classifier sits on top', correct: true },
            { label: 'A decoder only, generating the category name word by word', why: 'That works too (and with a big chat model it’s often easiest), but it’s overkill: you don’t need to generate text.' },
            { label: 'An encoder–decoder', why: 'Those turn one sequence into another sequence. A single label doesn’t need a decoder.' },
          ]}
          explain="In practice, decoder-only models have taken over almost everything because one big model can do every task by writing its answer. Encoders remain popular where speed and cost matter."
        />
      </Step>
    </Lesson>
  )
}
